import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Modal from "../../components/modal/modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faCopy,
  faGlobe,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";

// 🔹 Иконки для социальных сетей
import {
  faInstagram,
  faFacebook,
  faLinkedin,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";

// 🔹 Компонент для копирования ID пользователя
const UserIdCopy = ({ userId }) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(userId.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
      const textArea = document.createElement("textarea");
      textArea.value = userId.toString();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-500 text-lg font-medium">ID: {userId}</span>
      <button onClick={handleCopy}>
        {copied ? (
          <>
            <span className="text-[#006674]"> {t("copied")}</span>
          </>
        ) : (
          <>
            <FontAwesomeIcon className="text-xl text-gray-500" icon={faCopy} />
          </>
        )}
      </button>
    </div>
  );
};

// 🔹 Компонент для отображения телефонов
const PhoneNumbersField = ({ phones }) => {
  const validPhones = phones.filter((phone) => phone?.trim());

  if (validPhones.length === 0) return null;

  return (
    <div className="w-full mb-6">
      <div className="space-y-1">
        {validPhones.map((phone, index) => (
          <p
            key={index}
            className="text-lg flex justify-start items-center gap-2 text-[#006674]"
          >
            <div className="bg-blue-500 text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faPhone} />
            </div>
            <span className="break-all">{phone}</span>
          </p>
        ))}
      </div>
    </div>
  );
};

// 🔹 Компонент для Email
const EmailField = ({ email }) => {
  if (!email?.trim()) return null;

  return (
    <div className="w-full mb-6">
      <div className="flex items-center gap-3">
        <div className="bg-blue-500 text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center flex-shrink-0">
          <FontAwesomeIcon icon={faEnvelope} />
        </div>
        <a
          href={`mailto:${email}`}
          className="text-xl text-[#006674] hover:underline break-all"
        >
          {email}
        </a>
      </div>
    </div>
  );
};

// 🔹 Компонент для отображения ссылок
const LinksField = ({ links }) => {
  const validLinks = links.filter((link) => link?.trim());

  if (validLinks.length === 0) return null;

  return (
    <div className="w-full mb-2">
      <div className="space-y-1">
        {validLinks.map((link, index) => (
          <a
            key={index}
            href={link.startsWith("http") ? link : `https://${link}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex justify-start items-center gap-2 text-xl text-blue-600 hover:underline"
          >
            <div className="bg-blue-500 text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <span className="break-all">{link}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

// 🔹 Компонент для социальных сетей
const SocialMediaField = ({ socialMedia }) => {
  const socialPlatforms = [
    {
      key: "facebook",
      icon: faFacebook,
      color: "bg-blue-600",
    },
    {
      key: "instagram",
      icon: faInstagram,
      color: "bg-pink-500",
    },
    {
      key: "linkedin",
      icon: faLinkedin,
      color: "bg-blue-700",
    },
    {
      key: "youtube",
      icon: faYoutube,
      color: "bg-red-600",
    },
  ];

  const validSocialMedia = socialPlatforms.filter((platform) => {
    const value = socialMedia && socialMedia[platform.key];
    return value && typeof value === "string" && value.trim();
  });

  if (validSocialMedia.length === 0) return null;

  return (
    <div className="w-full mb-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-6">
        {validSocialMedia.map((platform) => {
          const value = socialMedia[platform.key];
          return (
            <a
              key={platform.key}
              href={value.startsWith("http") ? value : `https://${value}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-xl"
            >
              <div
                className={`${platform.color} text-white w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0`}
              >
                <FontAwesomeIcon icon={platform.icon} />
              </div>
              <span className="text-[#006674] hover:underline break-all">
                {platform.key.charAt(0).toUpperCase() + platform.key.slice(1)}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

// 🔹 Поле для названия работы
const JobTitleField = ({ jobTitle, show }) => {
  const { t } = useTranslation();

  if (!show || !jobTitle?.trim()) return null;

  return (
    <div className="w-full mt-12 mb-10">
      <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
        {t("Position")}
      </h2>
      <p className="text-lg break-words">{jobTitle}</p>
    </div>
  );
};

// 🔹 Поле для названия услуги
const ServiceNameField = ({ serviceName, show }) => {
  const { t } = useTranslation();
  if (!show || !serviceName?.trim()) return null;

  return (
    <div className="w-full mt-12 mb-10">
      <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
        {t("service")}
      </h2>
      <p className="text-lg break-words">{serviceName}</p>
    </div>
  );
};

// 🔹 Компонент для отображения сфер
const SphereField = ({ value, options }) => {
  const selectedOption = options.find((opt) => opt.key === value);
  if (!selectedOption) return null;

  return (
    <div className="w-auto max-sm:w-full">
      <p className="text-lg">{selectedOption.label}</p>
    </div>
  );
};

// 🔹 Группа для отображения типа услуги
const ServiceTypeField = ({ serviceType }) => {
  const { t } = useTranslation();

  const options = [
    { key: "offer_services", label: t("offerservices") },
    { key: "search_work", label: t("searchworktitle") },
  ];

  const selectedOption = options.find((opt) => opt.key === serviceType);
  if (!selectedOption) return null;

  return (
    <div className="w-full mb-3">
      <p className="py-2 text-[#006674] text-3xl max-sm:text-2xl font-bold uppercase break-words">
        {selectedOption.label}
      </p>
    </div>
  );
};

// 🔹 Вспомогательная функция для парсинга языков
const parseLanguages = (languagesData) => {
  if (!languagesData) return [];
  if (Array.isArray(languagesData)) return languagesData;
  if (typeof languagesData === "string") {
    try {
      const parsed = JSON.parse(languagesData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// 🔹 Компонент для отображения языков
const LanguagesField = ({ languages }) => {
  const validLanguages = languages.filter((lang) => lang.name?.trim());
  const { t } = useTranslation();

  if (validLanguages.length === 0) return null;

  return (
    <div className="w-full max-w-[500px] mb-10">
      <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
        {t("languages")}
      </h2>
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[400px] mb-6 border border-gray-300">
          <thead>
            <tr className="bg-[#006674] text-white">
              <th className="text-sm px-2 py-1 border border-gray-300">
                {t("language")}
              </th>
              <th className="text-sm px-2 py-1 border border-gray-300">
                {t("languageselect1")}
              </th>
              <th className="text-sm px-2 py-1 border border-gray-300">
                {t("languageselect2")}
              </th>
            </tr>
          </thead>
          <tbody>
            {validLanguages.map((lang, i) => (
              <tr key={i}>
                <td className="px-2 text-center py-1 border border-gray-300">
                  {lang.name ? t(`languageslist.${lang.name}`) : "-"}
                </td>
                <td className="px-2 text-center py-1 border border-gray-300">
                  {lang.level ? t(`levels.${lang.level}`) : "-"}
                </td>
                <td className="px-2 text-center py-1 border border-gray-300">
                  {lang.level2 ? t(`levels2.${lang.level2}`) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 🔹 Компонент для отображения текстового поля
const TextField = ({
  value,
  label,
  isTextArea = false,
  width = "w-[400px] max-sm:w-full",
}) => {
  if (!value?.trim()) return null;

  return (
    <div className={`${width} mb-10`}>
      <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
        {label}
      </h2>
      {isTextArea ? (
        <p className="text-lg whitespace-pre-wrap break-words">{value}</p>
      ) : (
        <p className="text-lg break-words">{value}</p>
      )}
    </div>
  );
};

// 🔹 Основная страница просмотра кандидата
const ViewCandidatePage = () => {
  const [modal, setModal] = useState({
    show: false,
    title: "",
    text: "",
    success: true,
  });
  const { t } = useTranslation();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // 🔹 Базовые данные
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [gender, setGender] = useState("");

  // 🔹 Доп. информация
  const [jobTitle, setJobTitle] = useState("");
  const [jobPeriod, setJobPeriod] = useState("");
  const [jobTitles, setJobTitles] = useState("");
  const [education, setEducation] = useState("");
  const [extraEducation, setExtraEducation] = useState("");
  const [awards, setAwards] = useState("");
  const [volunteer, setVolunteer] = useState("");
  const [languages, setLanguages] = useState([]);
  const [skills, setSkills] = useState("");
  const [computerKnowledge, setComputerKnowledge] = useState("");
  const [extraInfo, setExtraInfo] = useState("");

  // 🔹 Услуга и сферы
  const [serviceType, setServiceType] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [mainSphere, setMainSphere] = useState("");
  const [additionalSphere, setAdditionalSphere] = useState("");

  // 🔹 Контакты
  const [phones, setPhones] = useState([""]);
  const [email, setEmail] = useState("");
  const [links, setLinks] = useState([""]);
  const userid = localStorage.getItem("userId");
  const viewerId = userid ? Number(userid) : NaN;
  const isViewerIdValid = Number.isFinite(viewerId) && viewerId > 0;
  
  // 🔹 Проверка: смотрит ли пользователь сам себя
  const isOwnProfile = isViewerIdValid && viewerId === Number(id);

  const normalizeLiked = (value) => {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      value === "true" ||
      value === "yes"
    );
  };
  
  // 🔹 Социальные сети
  const [socialMedia, setSocialMedia] = useState({
    instagram: "",
    facebook: "",
    linkedin: "",
    youtube: "",
  });
  const [liked, setLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);

  const checkLikeStatus = useCallback(async () => {
    if (!id || !isViewerIdValid || isOwnProfile) return;
    try {
      const response = await fetch(
        `/api/check_like.php?user_id=${id}&liked_by=${viewerId}`
      );
      const data = await response.json();
      if (data?.success) setLiked(normalizeLiked(data.liked));
    } catch (err) {
      console.error("Ошибка проверки лайка:", err);
    }
  }, [id, isViewerIdValid, viewerId, isOwnProfile]);

  const handleLike = async () => {
    if (isLiking) return;

    if (!user?.id) return;
    if (!isViewerIdValid) {
      setModal({
        show: true,
        title: t("error"),
        text: t("pleaseregister") || "Please log in",
        success: false,
      });
      return;
    }

    setIsLiking(true);
    try {
      const response = await fetch("/api/like_user.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.id,
          liked_by: viewerId,
        }),
      });

      const data = await response.json();

      if (data.success) {
      const nextLiked =
        typeof data.liked !== "undefined" ? normalizeLiked(data.liked) : null;

      if (nextLiked !== null) {
        setLiked(nextLiked);
      } else {
        await checkLikeStatus();
      }

      setModal({
        show: true,
        title: t("success") || "Success",
        text:
          nextLiked === null
            ? t("like_status_updated") || "Статус лайка обновлён"
            : nextLiked
              ? t("candidate_added_to_likes") || "Кандидат добавлен в лайки!"
              : t("like_removed") || "Лайк удалён!",
        success: true,
      });
    } else {
      setModal({
        show: true,
        title: t("error"),
        text: `${t("error_message") || "Ошибка:"} ${data.message}`,
        success: false,
      });
    }
  } catch (err) {
    console.error("Ошибка при лайке:", err);
    setModal({
      show: true,
      title: t("error"),
      text: t("like_send_error") || "Ошибка при отправке лайка",
      success: false,
    });
  } finally {
    setIsLiking(false);
  }
};

  // В useEffect после загрузки пользователя
  useEffect(() => {
    if (user && !isOwnProfile) checkLikeStatus();
  }, [user, checkLikeStatus, isOwnProfile]);
  
  // 🔹 Опции для сфер деятельности
  const sphereOptions = t("filtres", { returnObjects: true }).map(
    (item, index) => ({
      key: `sphere_${index}`,
      label: item,
    })
  );

  // 🔹 Загружаем пользователя по ID из URL
  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (!id) {
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          `/api/get_user.php?id=${id}`
        );
        const data = await response.json();

        if (data.success) {
          setUser(data.user);

          // Основные поля
          setName(data.user.name || "");
          setLastName(data.user.lastName || "");
          setGender(data.user.gender || "");

          // Дата рождения
          if (data.user.birthday) {
            const parts = data.user.birthday.split("-");
            setBirthYear(parts[0] || "");
            setBirthMonth(parts[1] || "");
            setBirthDay(parts[2] || "");
          }

          // Поля из extra_info
          setJobTitle(data.user.jobTitle || "");
          setJobPeriod(data.user.jobPeriod || "");
          setJobTitles(data.user.jobTitles || "");
          setEducation(data.user.education || "");
          setExtraEducation(data.user.extraEducation || "");
          setAwards(data.user.awards || "");
          setVolunteer(data.user.volunteer || "");

          // Языки
          setLanguages(parseLanguages(data.user.languages));

          setSkills(data.user.skills || "");
          setComputerKnowledge(data.user.computerKnowledge || "");
          setExtraInfo(data.user.extraInfo || "");

          // Услуга и сферы
          setServiceType(data.user.serviceType || "");
          setServiceName(data.user.serviceName || "");
          setMainSphere(data.user.mainSphere || "");
          setAdditionalSphere(data.user.additionalSphere || "");

          // Контакты
          if (data.phones && data.phones.length > 0) {
            setPhones(data.phones.map((p) => p.phone || ""));
          }
          if (data.websites && data.websites.length > 0) {
            setLinks(data.websites.map((w) => w.url || ""));
          }

          // Социальные сети и email
          if (data.social_media) {
            setEmail(data.social_media.contact_email || "");
            setSocialMedia({
              instagram: data.social_media.instagram || "",
              facebook: data.social_media.facebook || "",
              linkedin: data.social_media.linkedin || "",
              youtube: data.social_media.youtube || "",
            });
          } else {
            setEmail("");
            setSocialMedia({
              instagram: "",
              facebook: "",
              linkedin: "",
              youtube: "",
            });
          }
        } 
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  if (isLoading)
    return <p className="text-center text-white mt-10">{t("loading")}...</p>;

  return (
    <section className="min-h-screen">
      {modal.show && (
        <Modal
          title={modal.title}
          text={modal.text}
          success={modal.success}
          actions={[
            {
              label: t("ok") || "OK",
              onClick: () => setModal((prev) => ({ ...prev, show: false })),
              success: !modal.success,
            },
          ]}
          onClose={() => setModal((prev) => ({ ...prev, show: false }))}
        />
      )}
      <div className="w-[1000px] max-xl:w-full m-auto bg-white shadow-2xl p-3 shadow-black border-[2px] border-[#006674] flex flex-col items-start">
        {/* 🔹 ВЕРХНЯЯ ЧАСТЬ - ИСПРАВЛЕНО */}
        <div className="flex w-full opacity-0 animate-[showtitle_1s_forwards] justify-start gap-3 items-start flex-wrap max-sm:flex-col">
          {/* Аватар */}
          <div className="flex justify-start items-center gap-3">
             <div className="w-44 h-44 max-sm:w-32 max-sm:h-32 translate-y-1 overflow-hidden rounded-2xl border-4 border-white shadow-lg mb-8 max-sm:mb-4 flex-shrink-0">
            <img
              src={
                user.photo
                  ? `/api/uploads/${user.photo}`
                  : "/default-avatar.png"
              }
              alt="User Avatar"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Имя и фамилия */}
              <div className="flex flex-col gap-2">
                {name && (
                  <p className="text-[24px] text-[#006674] font-semibold break-words">
                    {name}
                  </p>
                )}
                {lastName && (
                  <p className="text-[24px] text-[#006674] font-semibold break-words">
                    {lastName}
                  </p>
                )}
              </div>
              {/* Кнопка лайка */}
              {!isOwnProfile && (
                <button
                  onClick={handleLike}
                  disabled={isLiking}
                  className={`flex-shrink-0 w-[40px] h-[40px] border-[2px] border-white shadow-lg shadow-neutral-500 rounded-full flex items-center justify-center transition-all duration-300 ${
                    liked
                      ? "bg-red-500 text-white cursor-pointer hover:bg-red-600"
                      : isLiking
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#006674] text-white cursor-pointer hover:bg-[#004d5a]"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faHeart}
                    className="text-white"
                  />
                </button>
              )}
          </div>
         

          {/* Информация пользователя */}
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            {/* Имя, фамилия, дата, пол и кнопка лайка */}
            <div className="flex justify-start items-start gap-6 max-sm:gap-3 flex-wrap">
              {/* Дата и пол */}
              <div className="flex flex-col gap-2">
                {birthDay && birthMonth && birthYear && (
                  <p className="text-lg">{`${birthDay}-${birthMonth}-${birthYear}`}</p>
                )}
                {gender && (
                  <p className="text-lg">
                    {{
                      male: t("male"),
                      female: t("female"),
                      other: t("other"),
                    }[gender] || gender}
                  </p>
                )}
              </div>
            </div>
            
            {/* ID пользователя */}
            <UserIdCopy userId={user.id} />
          </div>
        </div>

        <div className="p-3 opacity-0 animate-[showtitle_1s_forwards] w-full">
          {/* Тип услуги */}
          <ServiceTypeField serviceType={serviceType} />

          {/* Заголовок раздела работы/услуг */}
          {(serviceType === "search_work" && jobTitles) ||
          (serviceType === "offer_services" && serviceName) ? (
            <h2 className="text-3xl max-sm:text-2xl font-bold text-[#006674] uppercase mb-8">
              {t("thisjobstitle")}
            </h2>
          ) : null}

          {/* Поле для названия работы */}
          <JobTitleField
            jobTitle={jobTitles}
            show={serviceType === "search_work"}
          />

          {/* Поле для названия услуги */}
          <ServiceNameField
            serviceName={serviceName}
            show={serviceType === "offer_services"}
          />

          {/* Сферы деятельности */}
          {(mainSphere || additionalSphere) && (
            <>
              <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
                {t("industry")}
              </h2>
              <div className="w-auto flex justify-start items-center gap-3 mb-10 max-sm:flex-col max-sm:items-start">
                <SphereField value={mainSphere} options={sphereOptions} />
                <SphereField value={additionalSphere} options={sphereOptions} />
              </div>
            </>
          )}

          {/* Опыт работы */}
          <TextField value={jobTitle} label={t("workexperiance")} />
          <TextField value={jobPeriod} label="" />

          {/* Образование и остальные поля */}
          <TextField
            value={education}
            label={t("Education")}
            isTextArea={true}
          />
          <TextField
            value={extraEducation}
            label={t("extraeducation")}
            isTextArea={true}
          />
          <TextField
            value={awards}
            label={t("ACHIEVEMENTS AND AWARDS")}
            isTextArea={true}
          />
          <TextField
            value={volunteer}
            label={t("Volunteer")}
            isTextArea={true}
          />

          {/* Языки */}
          <LanguagesField languages={languages} />

          <TextField value={skills} label={t("skills")} isTextArea={true} />
          <TextField
            value={computerKnowledge}
            label="Computer Knowledge"
            isTextArea={true}
          />
          <TextField
            value={extraInfo}
            label={t("extraInfo")}
            isTextArea={true}
          />

          {/* Контакты */}
          {(phones.some((phone) => phone?.trim()) ||
            email?.trim() ||
            links.some((link) => link?.trim()) ||
            Object.values(socialMedia).some((value) => value?.trim())) && (
            <h1 className="text-xl font-bold uppercase text-[#006674] mb-2">
              {t("contacts")}
            </h1>
          )}

          {/* Контакты: телефоны */}
          <PhoneNumbersField phones={phones} />

          {/* Email */}
          <EmailField email={email} />

          {/* Ссылки */}
          <LinksField links={links} />

          {/* Социальные сети */}
          <SocialMediaField socialMedia={socialMedia} />
        </div>
      </div>
    </section>
  );
};

export default ViewCandidatePage;